import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { tokenStore } from "@/lib/token";
import { Lock, ArrowRight, LogIn, Eye, EyeOff, ShieldCheck, ArrowLeft, Mail, MessageCircle, KeyRound } from "lucide-react";

export default function PasswordLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Certains comptes n'ont pas de mot de passe VantaPulse mais ont l'A2F activée (voir
  // needs_password=2 / needs_password_then_2fa côté backend) : impossible de leur
  // proposer l'étape mot de passe, on démarre directement sur la connexion par code et,
  // une fois connectés, on les envoie sur l'onboarding pour en définir un.
  const noPasswordAccount = sessionStorage.getItem("oauth_needs_password_setup_after") === "1";

  // Alternative au mot de passe : un code à 6 chiffres envoyé par email ou par MP
  // Discord (voir /auth/login-code sur le backend).
  const [mode, setMode] = useState(noPasswordAccount ? "code" : "password"); // "password" | "code"
  const [codeChannel, setCodeChannel] = useState("email"); // "email" | "discord"
  const [codeSent, setCodeSent] = useState(false);
  const [loginCode, setLoginCode] = useState("");

  const [twoFAState, setTwoFAState] = useState(null); // { methods, pending_token, method }
  const [twoFACode, setTwoFACode] = useState("");

  // `data` est la réponse de /auth/verify-password (sans A2F) ou /auth/2fa/login-verify :
  // c'est seulement à ce moment-là qu'un jeton pleinement valide est délivré (voir
  // create_oauth_pending_token côté backend, qui limitait le jeton reçu après l'OAuth
  // à cette seule vérification).
  const finishLogin = (data) => {
    login(data.access_token, data.user);
    toast.success("Connexion réussie");
    // Compte sans mot de passe VantaPulse (voir noPasswordAccount ci-dessus) : direction
    // onboarding pour en définir un, maintenant que l'A2F a été validée.
    const destination = noPasswordAccount ? "/onboarding" : "/";
    sessionStorage.removeItem("oauth_needs_password_setup_after");
    // Rechargement complet plutôt qu'une navigation React Router : cette étape suit
    // toujours un rechargement plein (redirection OAuth), et un simple navigate("/")
    // laissait parfois la page vide sur mobile (fond visible, rien d'autre) tant que
    // l'app n'était pas rechargée manuellement.
    // Safari iOS ignore parfois une navigation déclenchée directement dans un callback
    // asynchrone (juste après la réponse réseau de vérification du mot de passe) : la
    // repousser d'un tick via setTimeout, hors de la promesse, la rend fiable.
    setTimeout(() => { window.location.href = destination; }, 0);
  };

  const handlePasswordLogin = async () => {
    if (!password) {
      toast.error("Veuillez entrer votre mot de passe");
      return;
    }

    setLoading(true);
    try {
      const token = tokenStore.get();
      const response = await axios.post(`${API}/auth/verify-password`, {
        password,
        auth_header: `Bearer ${token}`
      });

      if (response.data.requires_2fa) {
        setTwoFAState({
          methods: response.data.methods,
          pending_token: response.data.pending_token,
          method: response.data.methods[0],
        });
        return;
      }

      if (response.data.access_token) {
        finishLogin(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  const requestLoginCode = async (channel) => {
    setCodeChannel(channel);
    setLoading(true);
    try {
      const token = tokenStore.get();
      await axios.post(`${API}/auth/login-code/request`, {
        channel,
        auth_header: `Bearer ${token}`,
      });
      setCodeSent(true);
      toast.success(channel === "email" ? "Code envoyé par email" : "Code envoyé sur Discord");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Impossible d'envoyer le code");
    } finally {
      setLoading(false);
    }
  };

  const verifyLoginCode = async () => {
    if (loginCode.length !== 6) return;
    setLoading(true);
    try {
      const token = tokenStore.get();
      const response = await axios.post(`${API}/auth/login-code/verify`, {
        channel: codeChannel,
        code: loginCode,
        auth_header: `Bearer ${token}`,
      });

      if (response.data.requires_2fa) {
        setTwoFAState({
          methods: response.data.methods,
          pending_token: response.data.pending_token,
          method: response.data.methods[0],
        });
        return;
      }

      if (response.data.access_token) {
        finishLogin(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const backToPassword = () => {
    setMode("password");
    setCodeSent(false);
    setLoginCode("");
  };

  const handleVerify2FA = async () => {
    if (twoFACode.length !== 6) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/2fa/login-verify`, {
        pending_token: twoFAState.pending_token,
        method: twoFAState.method,
        code: twoFACode,
      });
      finishLogin(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Code invalide");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key !== 'Enter') return;
    if (twoFAState) return handleVerify2FA();
    if (mode === "code") return codeSent ? verifyLoginCode() : undefined;
    return handlePasswordLogin();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute left-1/2 top-[18%] h-[380px] w-[760px] -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-600/20 via-indigo-600/15 to-violet-600/20 blur-[100px]"></div>
        <div className="absolute inset-0 opacity-[0.12] [background-image:linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] [background-size:44px_44px] [mask-image:radial-gradient(ellipse_at_center,#000_20%,transparent_70%)]"></div>
      </div>

      <div className="w-full max-w-md relative z-10">
        <a href="/" className="mb-7 flex items-center justify-center gap-2.5">
          <img src="/images/logo-navbar.png" alt="" className="h-9 w-9 rounded-full ring-1 ring-white/10" />
          <span className="font-heading text-lg font-bold tracking-tight text-white">VantaPulse</span>
        </a>
        <div className="bg-[#0f1019]/90 backdrop-blur-2xl border border-white/[0.09] rounded-[26px] p-8 sm:p-9 shadow-[0_40px_120px_-30px_rgba(59,130,246,0.45)] relative overflow-hidden animate-slideUp">
          {twoFAState ? (
            <>
              <div className="text-center mb-8 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/15 to-violet-500/15 blur-3xl rounded-full"></div>
                <div className="relative">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-violet-600 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-blue-900/40 ring-1 ring-white/10">
                    <ShieldCheck className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="font-heading text-[26px] font-bold tracking-tight text-white mb-2">
                    Vérification en deux étapes
                  </h2>
                  <p className="text-white/50 text-sm">
                    {twoFAState.method === "totp" ? "Code de votre application d'authentification" : "Code envoyé par email"}
                  </p>
                </div>
              </div>

              <input
                type="text"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyPress={handleKeyPress}
                placeholder="000000"
                maxLength={6}
                autoFocus
                className="w-full px-5 py-4 mb-6 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/25 focus:outline-none focus:border-blue-500/60 focus:bg-white/[0.05] focus:ring-4 focus:ring-blue-500/10 transition-all text-center text-3xl tracking-widest font-mono"
              />

              <button
                onClick={handleVerify2FA}
                disabled={loading || twoFACode.length !== 6}
                className="w-full py-3.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:brightness-110 active:scale-[0.99] text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-[0_10px_30px_-10px_rgba(79,70,229,0.8)]"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>Vérifier<ArrowRight className="w-5 h-5" /></>
                )}
              </button>

              {twoFAState.methods.length > 1 && (
                <div className="flex justify-center gap-3 mt-4">
                  {twoFAState.methods.map((m) => (
                    <button
                      key={m}
                      onClick={() => setTwoFAState((s) => ({ ...s, method: m }))}
                      className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${twoFAState.method === m ? "bg-white/10 text-white" : "text-white/40 hover:text-white/60"}`}
                    >
                      {m === "totp" ? "Authenticator" : "Email"}
                    </button>
                  ))}
                </div>
              )}

              <button
                onClick={() => setTwoFAState(null)}
                className="mt-6 text-sm text-white/40 hover:text-white/60 transition-colors flex items-center gap-1.5 mx-auto"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
            </>
          ) : mode === "password" ? (
            <>
              <div className="text-center mb-8 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/15 to-violet-500/15 blur-3xl rounded-full"></div>
                <div className="relative">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-violet-600 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-blue-900/40 ring-1 ring-white/10">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="font-heading text-[26px] font-bold tracking-tight text-white mb-2">
                    Connexion VantaPulse
                  </h2>
                  <p className="text-white/50 text-sm">Entrez votre mot de passe pour continuer</p>
                </div>
              </div>

              <div className="space-y-5 mb-8">
                <div className="group">
                  <label className="block text-[13px] text-white/75 mb-1.5 font-medium">Mot de passe VantaPulse</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="••••••••"
                      autoFocus
                      className="relative w-full px-5 py-4 pr-12 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/25 focus:outline-none focus:border-blue-500/60 focus:bg-white/[0.05] focus:ring-4 focus:ring-blue-500/10 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <button
                onClick={handlePasswordLogin}
                disabled={loading}
                className="w-full py-3.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:brightness-110 active:scale-[0.99] text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-[0_10px_30px_-10px_rgba(79,70,229,0.8)]"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Vérification...
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    Se connecter
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>

              <button
                type="button"
                data-testid="switch-to-code-login"
                onClick={() => setMode("code")}
                className="mt-4 w-full py-3 rounded-xl border border-white/10 text-white/70 hover:text-white hover:border-white/20 text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <KeyRound className="w-4 h-4" />
                Se connecter avec un code
              </button>

              <div className="mt-6 text-center space-y-3">
                <button
                  onClick={() => navigate("/forgot-password")}
                  className="block w-full text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium"
                >
                  Mot de passe oublié ?
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="text-sm text-white/40 hover:text-white/60 transition-colors"
                >
                  Retour à l'accueil
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-8 relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/15 to-violet-500/15 blur-3xl rounded-full"></div>
                <div className="relative">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-violet-600 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-blue-900/40 ring-1 ring-white/10">
                    <KeyRound className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="font-heading text-[26px] font-bold tracking-tight text-white mb-2">
                    Code de connexion
                  </h2>
                  <p className="text-white/50 text-sm">
                    {codeSent
                      ? (codeChannel === "email" ? "Entrez le code reçu par email" : "Entrez le code reçu sur Discord")
                      : "Choisissez où recevoir votre code"}
                  </p>
                </div>
              </div>

              {!codeSent ? (
                <div className="space-y-3 mb-6">
                  <button
                    type="button"
                    data-testid="request-code-email"
                    onClick={() => requestLoginCode("email")}
                    disabled={loading}
                    className="w-full py-3.5 rounded-xl flex items-center justify-center gap-3 font-medium text-white bg-white/[0.04] border border-white/10 hover:border-blue-400/40 hover:bg-blue-500/10 transition-all disabled:opacity-50"
                  >
                    <Mail className="w-5 h-5" />
                    Recevoir un code par email
                  </button>
                  <button
                    type="button"
                    data-testid="request-code-discord"
                    onClick={() => requestLoginCode("discord")}
                    disabled={loading}
                    className="w-full py-3.5 rounded-xl flex items-center justify-center gap-3 font-medium text-white bg-gradient-to-r from-[#5865F2]/15 to-[#7289da]/15 border border-[#5865F2]/30 hover:border-[#5865F2]/60 transition-all disabled:opacity-50"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Recevoir un code sur Discord
                  </button>
                  <p className="text-xs text-white/30 text-center">
                    Un compte Discord doit être connecté dans vos Paramètres pour recevoir un code sur Discord.
                  </p>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    value={loginCode}
                    onChange={(e) => setLoginCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    onKeyPress={handleKeyPress}
                    placeholder="000000"
                    maxLength={6}
                    autoFocus
                    className="w-full px-5 py-4 mb-6 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/25 focus:outline-none focus:border-blue-500/60 focus:bg-white/[0.05] focus:ring-4 focus:ring-blue-500/10 transition-all text-center text-3xl tracking-widest font-mono"
                  />
                  <button
                    onClick={verifyLoginCode}
                    disabled={loading || loginCode.length !== 6}
                    className="w-full py-3.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 hover:brightness-110 active:scale-[0.99] text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-3 disabled:opacity-50 shadow-[0_10px_30px_-10px_rgba(79,70,229,0.8)]"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <>Vérifier<ArrowRight className="w-5 h-5" /></>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => requestLoginCode(codeChannel)}
                    disabled={loading}
                    className="mt-4 w-full text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium disabled:opacity-50"
                  >
                    Renvoyer le code
                  </button>
                </>
              )}

              {!noPasswordAccount && (
                <button
                  type="button"
                  onClick={backToPassword}
                  className="mt-6 text-sm text-white/40 hover:text-white/60 transition-colors flex items-center gap-1.5 mx-auto"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Utiliser le mot de passe
                </button>
              )}
            </>
          )}
        </div>

        <style>{`
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
          .animate-slideUp {
            animation: slideUp 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          }
        `}</style>
      </div>
    </div>
  );
}
